import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Scissors, Footprints, Truck, MapPin, Star, Calendar, Heart, X, Shield, Bell, BellOff, IndianRupee, Clock, ArrowRight } from "lucide-react";

const TYPES = [
  { id: "groomer", label: "Groomers", icon: Scissors, color: "bg-brand", desc: "Bath, haircut, nails, ears." },
  { id: "walker", label: "Walkers", icon: Footprints, color: "bg-leaf", desc: "Daily walks, group walks, pickup." },
  { id: "mobile_vet_van", label: "Mobile Vet Vans", icon: Truck, color: "bg-sky", desc: "Vaccination & checkup drives near you." },
];

const CITIES = ["", "Noida", "Delhi", "Gurgaon", "Ghaziabad", "Mumbai", "Bengaluru", "Pune", "Hyderabad", "Chennai", "Kolkata"];

export default function Services() {
  const { user } = useAuth();
  const [tab, setTab] = useState("groomer");
  const [city, setCity] = useState("");
  const [items, setItems] = useState([]);
  const [visits, setVisits] = useState([]);
  const [booking, setBooking] = useState(null);     // service booking modal {provider}
  const [vanBooking, setVanBooking] = useState(null); // visit slot booking {visit}

  useEffect(() => {
    if (tab === "mobile_vet_van") {
      axios.get(`${API}/services`, { params: { type: tab, city: city || undefined } }).then(r => setItems(r.data.items));
      axios.get(`${API}/mobile-van/visits`, { params: { city: city || undefined } }).then(r => setVisits(r.data.items)).catch(() => setVisits([]));
    } else {
      axios.get(`${API}/services`, { params: { type: tab, city: city || undefined } }).then(r => setItems(r.data.items));
      setVisits([]);
    }
  }, [tab, city]);

  return (
    <div className="space-y-6" data-testid="services-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold">All pet pros, one app</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Services near you</h1>
          <p className="text-ink-soft text-sm">Vetted groomers, walkers and mobile vet vans — book in 60 seconds.</p>
        </div>
        {user && tab === "mobile_vet_van" && <VanWatchToggle/>}
      </header>

      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {TYPES.map(t => (
          <button key={t.id} data-testid={`svc-tab-${t.id}`} onClick={() => setTab(t.id)} className={`shrink-0 inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold border transition-all ${tab === t.id ? "bg-ink text-white border-ink" : "bg-surface border-line text-ink-soft hover:text-ink"}`}>
            <t.icon className="w-4 h-4"/> {t.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">City</span>
        <select value={city} onChange={e => setCity(e.target.value)} data-testid="svc-city" className="rounded-full bg-surface border border-line px-3 py-1.5 text-sm font-bold focus:border-brand outline-none">
          {CITIES.map(c => <option key={c} value={c}>{c || "All cities"}</option>)}
        </select>
      </div>

      {tab === "mobile_vet_van" && (
        <section className="space-y-4">
          <div>
            <h2 className="font-heading text-xl font-extrabold inline-flex items-center gap-2"><Calendar className="w-5 h-5 text-brand"/>Upcoming van visits</h2>
            <p className="text-xs text-ink-soft">A real vet on wheels coming to your neighbourhood. Book a slot.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visits.map(v => <VanVisitCard key={v.visit_id} v={v} onBook={() => setVanBooking(v)}/>)}
            {visits.length === 0 && <div className="col-span-full bg-surface border border-dashed border-line rounded-3xl p-8 text-center text-ink-soft">No upcoming visits in this city. <BellOff className="inline w-4 h-4 mx-1"/> Switch on notifications to be alerted when one is scheduled.</div>}
          </div>
          <div className="pt-4">
            <h2 className="font-heading text-xl font-extrabold">Mobile vet operators</h2>
            <p className="text-xs text-ink-soft">Click an operator to see their van profile and upcoming visits.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
              {items.map(p => <ProviderCard key={p.provider_id} p={p} hideBook/>)}
            </div>
          </div>
        </section>
      )}

      {tab !== "mobile_vet_van" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="provider-grid">
          {items.map(p => <ProviderCard key={p.provider_id} p={p} onBook={() => setBooking(p)}/>)}
          {items.length === 0 && <div className="col-span-full bg-surface border border-dashed border-line rounded-3xl p-8 text-center text-ink-soft">No {tab}s listed in this city yet. Try All cities, or invite one — share the app with a local pro!</div>}
        </div>
      )}

      {booking && <BookServiceModal provider={booking} onClose={() => setBooking(null)}/>}
      {vanBooking && <BookVanSlotModal visit={vanBooking} onClose={() => { setVanBooking(null); axios.get(`${API}/mobile-van/visits`, { params: { city: city || undefined } }).then(r => setVisits(r.data.items)); }}/>}
    </div>
  );
}

function ProviderCard({ p, onBook, hideBook }) {
  const typeMeta = TYPES.find(t => t.id === p.type);
  return (
    <article data-testid={`provider-${p.provider_id}`} className="bg-surface border border-line rounded-3xl overflow-hidden shadow-soft hover:-translate-y-1 transition-all">
      <div className="relative h-40 bg-surface-muted">
        <img src={p.photo} alt={p.name} className="w-full h-full object-cover"/>
        {p.verified && <span className="absolute top-3 left-3 bg-leaf text-ink text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-1 inline-flex items-center gap-1"><Shield className="w-3 h-3"/>Verified</span>}
        {typeMeta && <span className={`absolute top-3 right-3 ${typeMeta.color} text-white rounded-full p-2`}><typeMeta.icon className="w-4 h-4"/></span>}
      </div>
      <div className="p-4">
        <h3 className="font-heading text-lg font-extrabold leading-tight">{p.name}</h3>
        <div className="text-xs text-ink-soft mt-1 inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{p.city}{p.distance_km != null && ` · ${p.distance_km} km away`}</div>
        <p className="text-sm text-ink-soft mt-2 line-clamp-2">{p.bio}</p>
        <div className="flex items-center justify-between mt-3">
          <div className="font-heading text-xl font-extrabold">{p.rate > 0 ? `₹${p.rate}` : "—"}<span className="text-xs text-ink-soft font-bold ml-0.5">{p.rate_unit === "per_walk" ? "/walk" : (p.rate_unit === "per_hour" ? "/hr" : "/service")}</span></div>
          <div className="text-xs font-bold inline-flex items-center gap-1"><Star className="w-3 h-3 fill-sun text-sun"/>{p.rating}</div>
        </div>
        {!hideBook && onBook && (
          <button data-testid={`book-${p.provider_id}`} onClick={onBook} className="mt-3 w-full bg-brand text-white rounded-full py-2.5 text-sm font-bold hover:bg-brand-hover inline-flex items-center justify-center gap-1"><Calendar className="w-4 h-4"/> Book now</button>
        )}
      </div>
    </article>
  );
}

function VanVisitCard({ v, onBook }) {
  const dt = new Date(v.scheduled_at);
  const full = v.slots_booked >= v.total_slots;
  return (
    <article data-testid={`visit-${v.visit_id}`} className="bg-surface border border-line rounded-3xl p-5 shadow-soft hover:-translate-y-0.5 transition-all">
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand"><Truck className="w-4 h-4"/>Mobile Vet Van</div>
        {v.distance_km != null && <span className="text-[11px] font-bold bg-sun rounded-full px-2 py-0.5">{v.distance_km} km away</span>}
      </div>
      <h3 className="font-heading text-lg font-extrabold mt-1">{v.van_name}</h3>
      <div className="text-sm text-ink-soft mt-1 inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5"/>{v.location_label}</div>
      <div className="text-sm text-ink-soft inline-flex items-center gap-1 mt-1"><Calendar className="w-3.5 h-3.5"/>{dt.toLocaleDateString()} · <Clock className="w-3.5 h-3.5"/>{dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {v.duration_hours}h</div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {v.services_offered.map(s => <span key={s} className="text-[10px] font-bold uppercase tracking-wider bg-surface-muted rounded-full px-2 py-0.5">{s}</span>)}
      </div>
      <div className="mt-3 flex items-center justify-between">
        <div className="text-sm">
          <div className="font-heading text-xl font-extrabold">{v.slot_fee ? `₹${v.slot_fee}` : "Free"}<span className="text-xs font-bold text-ink-soft">/slot</span></div>
          <div className="text-[11px] text-ink-soft font-bold">{v.total_slots - v.slots_booked} / {v.total_slots} slots open</div>
        </div>
        <button data-testid={`book-slot-${v.visit_id}`} disabled={full} onClick={onBook} className={`rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-1 ${full ? "bg-surface-muted text-ink-soft" : "bg-brand text-white hover:bg-brand-hover"}`}>
          {full ? "Booked out" : "Book slot"} {!full && <ArrowRight className="w-3.5 h-3.5"/>}
        </button>
      </div>
    </article>
  );
}

function BookServiceModal({ provider, onClose }) {
  const [form, setForm] = useState({ pet_name: "", service_type: provider.services?.[0] || "", scheduled_at: "", duration_min: 60, notes: "" });
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/services/book`, { provider_id: provider.provider_id, ...form });
    setDone(true);
  };
  return (
    <Modal onClose={onClose} title={done ? "Booked!" : `Book ${provider.name}`}>
      {done ? (
        <div className="text-center py-4" data-testid="book-success">
          <Heart className="w-12 h-12 fill-brand text-brand mx-auto"/>
          <p className="font-heading text-xl font-extrabold mt-3">Request sent</p>
          <p className="text-sm text-ink-soft mt-1">{provider.name} will confirm shortly.</p>
          <button onClick={onClose} className="mt-4 bg-ink text-white rounded-full px-6 py-2.5 text-sm font-bold">Close</button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="book-svc-form">
          <FieldShell label="Pet name *"><input required value={form.pet_name} onChange={e => setForm({ ...form, pet_name: e.target.value })} className="li" data-testid="bk-pet"/></FieldShell>
          <FieldShell label="Service">
            <select value={form.service_type} onChange={e => setForm({ ...form, service_type: e.target.value })} className="li">
              {(provider.services || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </FieldShell>
          <FieldShell label="When *"><input required type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} className="li" data-testid="bk-when"/></FieldShell>
          <FieldShell label="Notes (optional)"><textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="li" placeholder="Anything we should know..."/></FieldShell>
          <div className="bg-leaf/40 rounded-2xl p-3 text-sm inline-flex items-center gap-2"><IndianRupee className="w-4 h-4"/> Estimated fee: <b>₹{provider.rate}</b></div>
          <button data-testid="bk-submit" className="w-full bg-brand text-white rounded-full py-3 font-bold">Send booking request</button>
        </form>
      )}
    </Modal>
  );
}

function BookVanSlotModal({ visit, onClose }) {
  const [form, setForm] = useState({ pet_name: "", service_needed: visit.services_offered[0] || "" });
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await axios.post(`${API}/mobile-van/visits/${visit.visit_id}/book`, form);
      setDone(true);
    } catch (ex) { setErr(ex.response?.data?.detail || "Could not book"); }
  };
  return (
    <Modal onClose={onClose} title={done ? "Slot booked!" : "Reserve a van slot"}>
      {done ? (
        <div className="text-center py-4" data-testid="book-success">
          <Truck className="w-12 h-12 text-brand mx-auto"/>
          <p className="font-heading text-xl font-extrabold mt-3">See you there 🐾</p>
          <p className="text-sm text-ink-soft mt-1">{new Date(visit.scheduled_at).toLocaleString()} · {visit.location_label}</p>
          <button onClick={onClose} className="mt-4 bg-ink text-white rounded-full px-6 py-2.5 text-sm font-bold">Close</button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="book-slot-form">
          <FieldShell label="Pet name *"><input required value={form.pet_name} onChange={e => setForm({ ...form, pet_name: e.target.value })} className="li"/></FieldShell>
          <FieldShell label="Service needed">
            <select value={form.service_needed} onChange={e => setForm({ ...form, service_needed: e.target.value })} className="li">
              {visit.services_offered.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </FieldShell>
          {err && <div data-testid="book-slot-error" className="bg-brand/10 border border-brand/30 rounded-2xl p-3 text-sm text-brand font-bold">{err}</div>}
          <button data-testid="vsb-submit" className="w-full bg-brand text-white rounded-full py-3 font-bold">Reserve slot</button>
        </form>
      )}
    </Modal>
  );
}

function VanWatchToggle() {
  const [watching, setWatching] = useState(null);
  useEffect(() => { axios.get(`${API}/mobile-van/watch`).then(r => setWatching(!!r.data?.user_id)); }, []);
  const toggle = async () => {
    if (watching) { await axios.delete(`${API}/mobile-van/watch`); setWatching(false); }
    else {
      // Use browser geolocation if available; otherwise default to Noida
      const onPos = async (lat, lng) => { await axios.post(`${API}/mobile-van/watch`, { city: "", lat, lng, radius_km: 15 }); setWatching(true); };
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (p) => onPos(p.coords.latitude, p.coords.longitude),
          () => onPos(28.5355, 77.3910),
          { timeout: 5000 }
        );
      } else { onPos(28.5355, 77.3910); }
    }
  };
  if (watching === null) return null;
  return (
    <button data-testid="van-watch-toggle" onClick={toggle} className={`rounded-full px-3 py-2 text-xs font-bold inline-flex items-center gap-1.5 border ${watching ? "bg-leaf text-ink border-leaf" : "bg-surface border-line text-ink-soft"}`}>
      {watching ? <><Bell className="w-3.5 h-3.5"/>Notifying you of nearby vans</> : <><BellOff className="w-3.5 h-3.5"/>Notify me when a van visits</>}
    </button>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl font-extrabold">{title}</h3>
          <button onClick={onClose}><X className="w-5 h-5"/></button>
        </div>
        {children}
        <style>{`.li{width:100%;border:1px solid #EAE0D5;border-radius:14px;padding:0.55rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.li:focus{border-color:#FF6B35;}`}</style>
      </div>
    </div>
  );
}

const FieldShell = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
